import { useState } from 'react'

import { Group } from '@/entities/groups/models/Group'
import { User } from '@/entities/user/models/User'
import { SubscriptionDto } from '@/shared/apis/fastmile/models'
import { Icon } from '@/shared/components'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { pxToRem } from '@/shared/styles/themes'
import { faSignOut } from '@fortawesome/pro-light-svg-icons'
import { faChevronDown, faChevronRight, faCircleHalfStroke, faGem, faUserGroup } from '@fortawesome/pro-regular-svg-icons'
import { faRectanglePro } from '@fortawesome/pro-solid-svg-icons'
import { Avatar, Box, Divider, styled, Typography } from '@mui/material'

import { NavItemSelector } from './NavItemSelector'

type ProfileDropdownProps = {
  user: User
  activeGroup: Group | undefined
  groups: Group[]
  subscription: SubscriptionDto | null
  isNavOpen: boolean
  onGroupSelect: (value: string) => void
  onManageSubscription: () => void
  onUpgradeSubscription: () => void
  toggleColorMode: () => void
  logout: () => void
  isDarkTheme: boolean
}


export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  user,
  activeGroup,
  groups,
  subscription,
  isNavOpen,
  onGroupSelect,
  onManageSubscription,
  onUpgradeSubscription,
  toggleColorMode,
  logout,
  isDarkTheme,
}) => {
  const [isGroupSubmenuOpen, setIsGroupSubmenuOpen] = useState(false)

  // Custom button content for both expanded and collapsed modes
  const ProfileButtonContent = () => {
    if (!isNavOpen) {
      return (
        <StyledCollapsedProfileContainer>
          <StyledAvatar>
            {user?.clientName?.split(' ').map((word: string) => word.charAt(0)).join('') || '?'}
          </StyledAvatar>
        </StyledCollapsedProfileContainer>
      )
    }

    return (
      <StyledProfileContainer>
        <StyledAvatar>
          {user?.clientName?.split(' ').map((word: string) => word.charAt(0)).join('') || '?'}
        </StyledAvatar>

        <StyledProfileInfo>
          <StyledGroupName variant="body2">
            {activeGroup?.name || 'No Group'}
          </StyledGroupName>
          <StyledClientName variant="caption">
            {user?.clientName}
          </StyledClientName>
        </StyledProfileInfo>

        <StyledExpandIndicator icon={faChevronDown} />
      </StyledProfileContainer>
    )
  }

  return (
    <NavItemSelector
      id="primary-nav-profile"
      value=""
      onSelectItem={() => { }}
      isNavOpen={true}
      slotProps={{
        button: {
          sx: { padding: 0, margin: 0, height: `${pxToRem(32)} !important` },
          children: <ProfileButtonContent />,
        },
        popper: {
          placement: isNavOpen ? 'bottom-start' : 'right-start',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} sx={{ padding: `${pxToRem(4)} ${pxToRem(8)}` }}>
        <StyledAvatar sx={{ width: pxToRem(36), height: pxToRem(36) }}>
          {user?.clientName?.split(' ').map((word: string) => word.charAt(0)).join('') || '?'}
        </StyledAvatar>

        <StyledProfileInfo>
          <StyledDropdownGroupName variant="body2">
            {activeGroup?.name || 'No Group'}
          </StyledDropdownGroupName>
          <StyledDropdownClientName variant="caption">
            {user?.clientName}
          </StyledDropdownClientName>
          <StyledDropdownUserName>
            {user?.name}
          </StyledDropdownUserName>
        </StyledProfileInfo>
      </Box>

      {/* Switch Group Option with Submenu */}
      <StyledSubmenuItem
        onMouseEnter={() => setIsGroupSubmenuOpen(true)}
        onMouseLeave={() => setIsGroupSubmenuOpen(false)}
      >
        <DropdownItem>
          <StyledMenuIcon icon={faUserGroup} />
          Switch Group
          <Box ml="auto">
            <Icon icon={faChevronRight} sx={{ fontSize: pxToRem(12) }} />
          </Box>
        </DropdownItem>

        {isGroupSubmenuOpen && (
          <StyledGroupSubmenu className="group-submenu">
            {groups.map((group) => (
              <DropdownItem
                key={group.id}
                data-value={group.id}
                isSelected={group.id === activeGroup?.id}
                onSelect={() => onGroupSelect(group.id.toString())}
              >
                {group.name}
              </DropdownItem>
            ))}
          </StyledGroupSubmenu>
        )}
      </StyledSubmenuItem>

      <Divider sx={{ mb: 1 }} />

      {/* Subscription Options */}
      {subscription?.stripeSubscriptionId ? (
        <DropdownItem onSelect={onManageSubscription}>
          <StyledMenuIcon icon={faRectanglePro} />
          Manage Subscription
        </DropdownItem>
      ) : (
        <DropdownItem onSelect={onUpgradeSubscription}>
          <StyledMenuIcon icon={faGem} />
          Upgrade to Premium
        </DropdownItem>
      )}

      <Divider />

      {/* Settings Options */}
      <DropdownItem onSelect={toggleColorMode}>
        <StyledMenuIcon icon={faCircleHalfStroke} />
        Switch to {isDarkTheme ? 'light' : 'dark'} mode
      </DropdownItem>

      <DropdownItem onSelect={logout}>
        <StyledMenuIcon icon={faSignOut} />
        Sign Out
      </DropdownItem>
    </NavItemSelector>
  )
}


const StyledMenuIcon = styled(Icon)`
  margin-right: ${pxToRem(6)};
  font-size: ${pxToRem(16)};
`
const StyledAvatar = styled(Avatar)`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  background-color: ${(props) => props.theme.palette.custom.sidebar.avatarBgColor};
  border-radius: ${pxToRem(4)};
`

const StyledProfileContainer = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  flex-direction: row;
  gap: ${pxToRem(8)};
  cursor: pointer;
  border-radius: ${pxToRem(4)};
  transition: background-color 0.2s ease;
`

const StyledProfileInfo = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: ${pxToRem(2)};
`

const StyledGroupName = styled(Typography)`
  color: ${(props) => props.theme.palette.common.white};
  font-weight: 600;
  font-size: ${pxToRem(13)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

const StyledClientName = styled(Typography)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  font-size: ${pxToRem(12)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

const StyledDropdownUserName = styled(Typography)`
  font-weight: 500;
  font-size: ${pxToRem(13)};
  margin-top: ${pxToRem(4)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

const StyledDropdownGroupName = styled(Typography)`
  font-weight: 600;
  font-size: ${pxToRem(13)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

const StyledDropdownClientName = styled(Typography)`
  font-size: ${pxToRem(12)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`


const StyledExpandIndicator = styled(Icon)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  font-size: ${pxToRem(12)};
  margin-right: ${pxToRem(4)};
  transition: transform 0.2s ease;
`

const StyledGroupSubmenu = styled(Box)`
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 200px;
  background-color: ${(props) => props.theme.palette.background.paper};
  box-shadow: ${(props) => props.theme.shadows[8]};
  border-radius: ${pxToRem(4)};
  z-index: 1000;
  border: 1px solid ${(props) => props.theme.palette.divider};
  max-height: ${pxToRem(300)};
  overflow-y: auto;
`

const StyledSubmenuItem = styled('div')`
  position: relative;
  
  &:hover .group-submenu {
    display: block;
  }
`

const StyledCollapsedProfileContainer = styled('div')`
  display: flex;
  justify-content: center;
`
